/**
 * Centralized activity-type to icon/label/color mapping.
 *
 * Canonical keys use hyphens to match the backend `ActivityType.key`
 * (see database/seeders/ActivityTypeSeeder.php). Underscored aliases are
 * accepted via normalization for legacy callers (Course Generator outline cards).
 *
 * Icons are Phosphor Vue components from @phosphor-icons/vue.
 */
import {
    PhArrowSquareOut,
    PhBookOpen,
    PhCardsThree,
    PhChatsCircle,
    PhCheckSquareOffset,
    PhFileText,
    PhInfo,
    PhLink,
    PhMicrophone,
    PhMonitor,
    PhPackage,
    PhPresentation,
    PhQuestion,
    PhUploadSimple,
    PhVideoCamera,
} from '@phosphor-icons/vue';

const DEFAULT_ACTIVITY_COLOR = '#6b7280';

export const ACTIVITY_ICON_MAP = Object.freeze({
    'slide-deck': PhPresentation,
    video: PhVideoCamera,
    'view-information': PhInfo,
    'file-submission': PhUploadSimple,
    'acknowledge-information': PhCheckSquareOffset,
    'discussion-thread': PhChatsCircle,
    flashcard: PhCardsThree,
    podcast: PhMicrophone,
    quiz: PhFileText,
    survey: PhQuestion,
    'e-book': PhBookOpen,
    'lti-tool': PhLink,
    'lti-resource-link': PhArrowSquareOut,
    presentation: PhMonitor,
    'e-learning-package': PhPackage,
});

export const ACTIVITY_LABEL_MAP = Object.freeze({
    'slide-deck': 'Slide Decks',
    video: 'Videos',
    'view-information': 'View Info',
    'file-submission': 'File Submission',
    'acknowledge-information': 'Acknowledge',
    'discussion-thread': 'Discussion Thread',
    flashcard: 'Flashcards',
    podcast: 'Podcast',
    quiz: 'Quiz',
    survey: 'Survey',
    'e-book': 'eBook',
    'lti-tool': 'LTI Tool',
    'lti-resource-link': 'LTI Resource Link',
    'e-learning-package': 'eLearning Packages',
});

/**
 * Singular display labels used to prefix activity titles in the course /
 * course-template navigation menus (e.g. "Video: Yamanacka Findings").
 * Distinct from ACTIVITY_LABEL_MAP, which is collection-styled (plural)
 * and used for tooltips/filter chips on the Course Generator outline.
 */
export const ACTIVITY_NAV_LABEL_MAP = Object.freeze({
    'slide-deck': 'Slide Deck',
    video: 'Video',
    'view-information': 'Info',
    'file-submission': 'File',
    'acknowledge-information': 'Acknowledge',
    'discussion-thread': 'Discussion',
    flashcard: 'Flashcards',
    podcast: 'Podcast',
    quiz: 'Quiz',
    survey: 'Survey',
    'e-book': 'eBook',
    'lti-tool': 'LTI Tool',
    'lti-resource-link': 'LTI Link',
    presentation: 'Presentation',
    'e-learning-package': 'eLearning Package',
});

const normalize = type => (typeof type === 'string' ? type.replace(/_/g, '-') : type);

/**
 * @param {string|null|undefined} type Activity type key (hyphen or underscore form).
 * @returns {string|null} Unicons name, or null when no mapping exists.
 *                       Callers decide on a fallback (the Course Generator card
 *                       uses 'file-alt'; the nav renders no icon).
 */
export function getActivityIcon(type) {
    return ACTIVITY_ICON_MAP[normalize(type)] ?? null;
}

export function getActivityLabel(type) {
    const key = normalize(type);
    return ACTIVITY_LABEL_MAP[key] ?? type ?? '';
}

/**
 * Singular nav-prefix label, e.g. `getActivityNavLabel('video')` → 'Video'.
 *
 * Returns null (rather than echoing the raw type) when no mapping exists,
 * so the caller can suppress the prefix entirely instead of rendering
 * "lti-resource-link: Foo" in the UI.
 *
 * @param {string|null|undefined} type Activity type key (hyphen or underscore form).
 * @returns {string|null}
 */
export function getActivityNavLabel(type) {
    return ACTIVITY_NAV_LABEL_MAP[normalize(type)] ?? null;
}

// eslint-disable-next-line no-unused-vars
export function getActivityColor(_type) {
    return DEFAULT_ACTIVITY_COLOR;
}
